import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Visibility as VisibilityIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useFechaOperativa } from '../../contexts/FechaOperativaContext';
import granjaService, {
  GranjaMovimientoListItem,
  GranjaMovimientoTipo,
} from '../../services/granjaService';
import { getApiErrorMessage } from '../../utils/apiError';
import { formatComprobanteTesoreria, toLocalDateStringYMD } from '../../utils/formatters';

const TIPOS: { value: '' | GranjaMovimientoTipo; label: string }[] = [
  { value: '', label: 'Todos' },
  { value: 'RECOLECCION', label: 'Recolección' },
  { value: 'CLASIFICACION', label: 'Clasificación' },
  { value: 'MORTANDAD', label: 'Mortandad' },
  { value: 'DESCARTE', label: 'Descarte' },
  { value: 'ALTA_LOTE', label: 'Alta lote' },
  { value: 'DESPACHO_PROD', label: 'Despacho producción' },
];

const defaultDesde = (hastaYmd: string): string => {
  const [y, m, d] = hastaYmd.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() - 30);
  return toLocalDateStringYMD(dt);
};

const formatFecha = (value?: string | null): string => {
  if (!value) return '—';
  const ymd = value.slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return value;
  return `${m[3]}/${m[2]}/${m[1]}`;
};

const comprobanteLabel = (row: GranjaMovimientoListItem): string => {
  const formatted = formatComprobanteTesoreria(
    row.letra || 'X',
    row.nroSuc ?? 0,
    row.nroComprobante > 0 ? row.nroComprobante : null,
  );
  if (formatted !== '-') return formatted;
  // Fallback si aún no hay numeración comercial
  return formatComprobanteTesoreria(row.letra || 'X', row.nroSuc ?? 0, row.nroFormulario);
};

const GranjaMovimientos: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const { fechaYmd } = useFechaOperativa();
  const diaOperativo = fechaYmd ?? toLocalDateStringYMD();

  const tipoFromUrl = (searchParams.get('tipo') || '').toUpperCase();
  const tipoInicial = TIPOS.some((t) => t.value === tipoFromUrl)
    ? (tipoFromUrl as GranjaMovimientoTipo)
    : '';

  const [desde, setDesde] = useState(() => defaultDesde(diaOperativo));
  const [hasta, setHasta] = useState(diaOperativo);
  const [tipo, setTipo] = useState<'' | GranjaMovimientoTipo>(tipoInicial);
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState<GranjaMovimientoListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = (searchParams.get('tipo') || '').toUpperCase();
    const next = TIPOS.some((x) => x.value === t) ? (t as GranjaMovimientoTipo) : '';
    setTipo(next);
    setPage(0);
  }, [searchParams]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await granjaService.getMovimientos({
        fechaDesde: desde || undefined,
        fechaHasta: hasta || undefined,
        tipo: tipo || undefined,
        searchTerm: search.trim() || undefined,
        pageNumber: page + 1,
        pageSize: rowsPerPage,
      });
      setRows(data.items);
      setTotal(data.totalCount);
    } catch (err: unknown) {
      enqueueSnackbar(getApiErrorMessage(err, 'Error al cargar movimientos'), { variant: 'error' });
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [desde, hasta, tipo, search, page, rowsPerPage, enqueueSnackbar]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleTipoChange = (value: '' | GranjaMovimientoTipo) => {
    setTipo(value);
    setPage(0);
    const next = new URLSearchParams(searchParams);
    if (value) next.set('tipo', value);
    else next.delete('tipo');
    setSearchParams(next, { replace: true });
  };

  const openDetalle = (nroFormulario: number) => {
    navigate(`/inventario/transferencias/${nroFormulario}`);
  };

  const emptyMessage = (
    <Typography variant="body2" color="text.secondary">
      Sin movimientos para los filtros indicados.
    </Typography>
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2}>
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontWeight: 600, color: '#1976d2', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
            >
              Movimientos Granja
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Comprobantes de stock del módulo (recolección, clasificación, aves, despacho, etc.).
            </Typography>

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              alignItems={{ md: 'center' }}
            >
              <TextField
                label="Desde"
                type="date"
                value={desde}
                onChange={(e) => {
                  setDesde(e.target.value);
                  setPage(0);
                }}
                InputLabelProps={{ shrink: true }}
                size="small"
                fullWidth
                sx={{ maxWidth: { md: 180 } }}
              />
              <TextField
                label="Hasta"
                type="date"
                value={hasta}
                onChange={(e) => {
                  setHasta(e.target.value);
                  setPage(0);
                }}
                InputLabelProps={{ shrink: true }}
                size="small"
                fullWidth
                sx={{ maxWidth: { md: 180 } }}
              />
              <FormControl size="small" fullWidth sx={{ minWidth: { md: 200 }, maxWidth: { md: 240 } }}>
                <InputLabel id="granja-mov-tipo">Tipo</InputLabel>
                <Select
                  labelId="granja-mov-tipo"
                  label="Tipo"
                  value={tipo}
                  onChange={(e) => handleTipoChange(e.target.value as '' | GranjaMovimientoTipo)}
                >
                  {TIPOS.map((t) => (
                    <MenuItem key={t.value || 'all'} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Buscar"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                placeholder="Comprobante, descripción o lote"
                size="small"
                fullWidth
              />
              <Button
                variant="contained"
                onClick={() => void load()}
                disabled={loading}
                fullWidth
                sx={{ maxWidth: { md: 140 }, flexShrink: 0 }}
              >
                {loading ? 'Cargando…' : 'Buscar'}
              </Button>
            </Stack>

            {/* Tarjetas en móvil / tablet angosta */}
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              {rows.length === 0 && !loading ? (
                emptyMessage
              ) : (
                <Stack spacing={1.5}>
                  {rows.map((row) => (
                    <Card
                      key={row.nroFormulario}
                      variant="outlined"
                      sx={{ borderRadius: 2, bgcolor: '#fafafa' }}
                    >
                      <CardContent sx={{ pb: 1, '&:last-child': { pb: 1 } }}>
                        <Stack spacing={1}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="flex-start"
                            spacing={1}
                          >
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                {comprobanteLabel(row)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatFecha(row.fechaEmision)}
                              </Typography>
                            </Box>
                            <Chip
                              size="small"
                              label={row.motivoLabel || row.codigoMotivo || '—'}
                              color="primary"
                              variant="outlined"
                            />
                          </Stack>
                          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                            {row.descripcion || '—'}
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Chip
                              size="small"
                              label={`Lote: ${row.codigosLote || '—'}`}
                              variant="outlined"
                            />
                            <Chip
                              size="small"
                              label={`Suc.: ${row.nroSuc || '—'}`}
                              variant="outlined"
                            />
                          </Stack>
                        </Stack>
                      </CardContent>
                      <Divider />
                      <CardActions sx={{ justifyContent: 'flex-end', px: 1.5, py: 0.5 }}>
                        <Button
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => openDetalle(row.nroFormulario)}
                        >
                          Ver
                        </Button>
                      </CardActions>
                    </Card>
                  ))}
                </Stack>
              )}
            </Box>

            {/* Tabla en desktop */}
            <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Comprobante</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell>Lote</TableCell>
                    <TableCell align="right">Suc.</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 && !loading ? (
                    <TableRow>
                      <TableCell colSpan={7}>{emptyMessage}</TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => (
                      <TableRow key={row.nroFormulario} hover>
                        <TableCell>{formatFecha(row.fechaEmision)}</TableCell>
                        <TableCell>{comprobanteLabel(row)}</TableCell>
                        <TableCell>{row.motivoLabel || row.codigoMotivo}</TableCell>
                        <TableCell>{row.descripcion || '—'}</TableCell>
                        <TableCell>{row.codigosLote || '—'}</TableCell>
                        <TableCell align="right">{row.nroSuc || '—'}</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={() => openDetalle(row.nroFormulario)}
                          >
                            Ver
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50]}
              labelRowsPerPage="Filas"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} de ${count !== -1 ? count : `más de ${to}`}`
              }
              sx={{
                '.MuiTablePagination-toolbar': {
                  flexWrap: 'wrap',
                  justifyContent: { xs: 'center', sm: 'flex-end' },
                  px: { xs: 0, sm: 2 },
                },
              }}
            />
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default GranjaMovimientos;
